import React, { useState } from 'react';
import {
  Card,
  Typography,
  Button,
  Container,
  Grid,
  Box,
  Chip,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Check,
  DirectionsCar,
  Smartphone,
  LocalTaxi
} from '@mui/icons-material';

const professionalPlansData = [
  {
    id: "pro-6",
    name: "Motorista 6 Lavagens",
    prices: {
      montly: {
        M: 224.90,
        G: 258.90
      },
      annual: {
        M: 2158.80,
        G: 2482.80
      }
    },
    description: "6 lavagens mensais",
    features: [
      "6 lavagens por mês",
      "Lavagem simples (externa + interna básica)",
      "Atendimento em lava-rápidos selecionados",
      "Ideal para uso profissional recorrente",
      "Sem permanência mínima",
      "Cancelamento a qualquer momento"
    ],
    badges: ["Uber", "99", "Táxi"],
    icon: DirectionsCar,
    popular: false,
    color: "#06b6d4"
  },
  {
    id: "pro-10",
    name: "Motorista 10 Lavagens",
    prices: {
      montly: {
        M: 287.90,
        G: 331.90
      },
      annual: {
        M: 2758.80,
        G: 3178.80
      }
    },
    description: "10 lavagens mensais",
    features: [
      "10 lavagens por mês",
      "Lavagem simples (externa + interna básica)",
      "Atendimento em lava-rápidos selecionados",
      "Ideal para uso profissional recorrente",
      "Sem permanência mínima",
      "Cancelamento a qualquer momento"
    ],
    badges: ["Uber", "99", "Táxi"],
    icon: DirectionsCar,
    popular: true,
    color: "#06b6d4"
  }
];

// Format price to Brazilian currency format
const formatPrice = (price: number) => {
  return price.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const ProfessionalPricingSection = () => {
  const [carType, setCarType] = useState<'M' | 'G'>('M');

  const currentPlans = professionalPlansData.map(plan => ({
    ...plan,
    price: plan.prices.montly[carType],
    annual: plan.prices.annual[carType]
  }));

  const handleCarTypeChange = (
    event: React.MouseEvent<HTMLElement>,
    newCarType: 'M' | 'G' | null,
  ) => {
    if (newCarType !== null) {
      setCarType(newCarType);
    }
  };

  return (
    <section id="planos-profissionais" className="section-spacing bg-background">
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }} className="component-spacing">
          <Chip
            icon={<LocalTaxi />}
            label="PLANOS PROFISSIONAIS"
            sx={{
              backgroundColor: 'rgba(6, 182, 212, 0.1)',
              color: '#06b6d4',
              fontWeight: 600,
              mb: 2
            }}
          />
          <Typography
            variant="h2"
            className="text-section"
            sx={{
              color: '#1a202c',
              mb: 2,
              fontSize: { xs: '2rem', md: '2.5rem', lg: '3rem' },
              fontWeight: 700
            }}
          >
            Planos para Motoristas Profissionais
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#64748b',
              maxWidth: '800px',
              mx: 'auto',
              mb: 4
            }}
          >
            Planos especiais com limite mensal de lavagens, ideal para motoristas de aplicativo
            (Uber, 99, Táxi). Atendimento exclusivo em lava-rápidos selecionados.
          </Typography>

          {/* Car Type Toggle */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: '#f8fafc',
            borderRadius: 2,
            p: 0.5,
            mb: 4,
            width: { xs: '100%', sm: 'auto' },
            maxWidth: { xs: '100%', sm: 'none' },
            mx: 'auto'
          }}>
            <ToggleButtonGroup
              value={carType}
              exclusive
              onChange={handleCarTypeChange}
              aria-label="tipo de carro"
              fullWidth={true}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 0.5, sm: 0 },
                '& .MuiToggleButton-root': {
                  px: { xs: 2, sm: 2, md: 3 },
                  py: { xs: 1.25, sm: 1.25, md: 1.5 },
                  fontWeight: 600,
                  border: 'none',
                  fontSize: { xs: '0.875rem', sm: '0.875rem', md: '1rem' },
                  whiteSpace: 'nowrap',
                  lineHeight: 1.5,
                  textAlign: 'center',
                  width: { xs: '100%', sm: 'auto' },
                  mb: { xs: 0.5, sm: 0 },
                  '&:last-child': {
                    mb: 0
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'white',
                    color: '#1a202c',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }
                }
              }}
            >
              <ToggleButton value="M" aria-label="carros M">
                <DirectionsCar sx={{ mr: 1, width: 16, height: 16, flexShrink: 0 }} />
                Carros M (Hatch/Sedan)
              </ToggleButton>
              <ToggleButton value="G" aria-label="carros G">
                <DirectionsCar sx={{ mr: 1, width: 16, height: 16, flexShrink: 0 }} />
                Carros G (SUV/Caminhonete)
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Plans Grid */}
        <Grid container spacing={3} sx={{ mb: 4, justifyContent: 'center' }}>
          {currentPlans.map((plan) => {
            const Icon = plan.icon;

            // const normalPriceAnnually = plan.price * 12;
            // const savings = normalPriceAnnually - plan.annual;

            return (
              <Grid item xs={12} md={6} lg={5} key={plan.id} sx={{ display: 'flex' }}>
                <Card
                  sx={{
                    position: 'relative',
                    p: 3,
                    boxShadow: plan.popular
                      ? '0 8px 30px rgba(6, 182, 212, 0.15)'
                      : '0 4px 20px rgba(30, 58, 138, 0.1)',
                    background: plan.popular
                      ? 'linear-gradient(145deg, rgba(6, 182, 212, 0.05) 0%, transparent 100%)'
                      : 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                    border: plan.popular ? '2px solid #06b6d4' : '1px solid #e2e8f0',
                    borderRadius: '10px',
                    transform: plan.popular ? 'scale(1.05)' : 'scale(1)',
                    transition: 'all 0.3s ease',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'visible',
                    '&:hover': {
                      boxShadow: '0 8px 30px rgba(30, 58, 138, 0.15)',
                      transform: plan.popular ? 'scale(1.05) translateY(-8px)' : 'translateY(-8px)'
                    }
                  }}
                >
                  {/* Popular Badge */}
                  {plan.popular && (
                    <Chip
                      label="Mais Popular"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: '#06b6d4',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        zIndex: 10
                      }}
                    />
                  )}

                  <Box sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', pt: 2 }}>
                    {/* Icon */}
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                        background: plan.color,
                        transition: 'transform 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.1)'
                        }
                      }}
                    >
                      <Icon sx={{ color: 'white', width: 32, height: 32 }} />
                    </Box>

                    {/* Plan Name */}
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: '#1a202c',
                        mb: 1
                      }}
                    >
                      {plan.name}
                    </Typography>

                    {/* Badges (Uber, 99, Táxi) */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      {plan.badges.map((badge) => (
                        <Chip
                          key={badge}
                          label={badge}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(6, 182, 212, 0.1)',
                            color: '#06b6d4',
                            fontWeight: 600,
                            fontSize: '0.7rem'
                          }}
                        />
                      ))}
                    </Box>

                    {/* Price */}
                    <Box sx={{ mb: 2 }}>
                      {/* Mensal */}
                      <Typography
                        sx={{
                          fontSize: '0.85rem',
                          color: '#64748b',
                          mb: 0.5
                        }}
                      >
                        Plano mensal
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: '1.4rem',
                          fontWeight: 400,
                          color: '#1a202c'
                        }}
                      >
                        R$ {formatPrice(plan.price)}<span style={{ fontSize: '0.9rem', fontWeight: 400 }}> /mês</span>
                      </Typography>

                      {/* Divider */}
                      <Box sx={{
                        my: 2,
                        borderTop: '1px dashed #e2e8f0',
                        position: 'relative'
                      }}>
                        <Chip
                          label="OU ANUAL"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: -10,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: '#64748b', //'#f1f5f9',
                            color: 'white',
                            fontSize: '0.7rem'
                          }}
                        />
                      </Box>

                      {/* Anual destaque */}
                      <Box sx={{
                        background: 'linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)',
                        border: '1px solid #67e8f9',
                        borderRadius: 2,
                        px: '7px',
                        py: 2.5,
                        marginTop: '28px'
                      }}>
                        <Typography
                          sx={{
                            fontSize: '1.6rem',
                            fontWeight: 800,
                            color: '#06b6d4',
                            lineHeight: 1.2
                          }}
                        >
                          R$ {formatPrice(plan.annual / 12)}<span style={{ fontSize: '0.9rem' }}> /mês*</span>
                        </Typography>

                        {/* Economia */}
                        {/* <Typography
                          sx={{
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            color: '#16a34a',
                            mt: 0.5
                          }}
                        >
                          Economize R$ {formatPrice(savings)}
                          <Typography
                            sx={{
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              color: '#16a34a',
                              mt: 0.5
                            }}>
                            (20% OFF)
                          </Typography>
                        </Typography> */}
                      </Box>
                    </Box>

                    {/* Description */}
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        mb: 3,
                        fontWeight: 600
                      }}
                    >
                      {plan.description}
                    </Typography>

                    {/* Features */}
                    <Box sx={{ mb: 3, flex: 1, textAlign: 'left' }}>
                      {plan.features.map((feature, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            mb: 1.5
                          }}
                        >
                          <Check sx={{
                            color: '#06b6d4',
                            width: 20,
                            height: 20,
                            mr: 1,
                            flexShrink: 0,
                            mt: 0.25
                          }} />
                          <Typography
                            variant="body2"
                            sx={{ color: '#1a202c', lineHeight: 1.6 }}
                          >
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* CTA Button */}
                    <Button
                      fullWidth
                      variant={plan.popular ? "contained" : "outlined"}
                      startIcon={<Smartphone />}
                      href="/app-download"
                      sx={{
                        fontWeight: 600,
                        backgroundColor: plan.popular ? '#06b6d4' : 'transparent',
                        color: plan.popular ? 'white' : '#06b6d4',
                        borderColor: '#06b6d4',
                        py: 1.5,
                        '&:hover': {
                          backgroundColor: plan.popular ? '#0891b2' : '#06b6d4',
                          color: 'white'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Assine no App
                    </Button>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Footer Note */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="body2"
            sx={{
              color: '#64748b',
              maxWidth: '700px',
              mx: 'auto'
            }}
          >
            <span style={{ fontWeight: 700 }}>📌 Planos exclusivos para motoristas profissionais</span>
            <br />
            Modalidade com limite mensal de lavagens e atendimento por lava-rápidos específicos.
            <br />
            <br />
            Ideal para motoristas de aplicativo (Uber, 99, Táxi) e profissionais que utilizam
            o veículo como ferramenta de trabalho. Todos os planos são cobrados mensalmente no cartão.
            O cancelamento pode ser feito a qualquer momento pelo app.
            <br />
            <br />
            <span style={{ fontWeight: 700 }}>* Valor cobrado no plano anual com parcela única.</span>
          </Typography>
        </Box>
      </Container>
    </section>
  );
};

export default ProfessionalPricingSection;
